<template>
  <div
    class="px-4 py-4 max-w-(--breakpoint-xl) m-auto"
    data-test-id="review-showcase-placeholder"
    aria-hidden="true"
  >
    <div
      class="flex flex-col md:flex-row items-center justify-center gap-12 md:gap-24 mb-8"
    >
      <ReviewShowcaseTitle
        :average-rating="averageRating"
        :review-count="getRealReviewsCount"
        :review-type="reviewType"
      />
      <ReviewBreakdown v-if="showBreakdown" :breakdown="reviewBreakdown!" />
    </div>

    <div
      class="overflow-hidden flex flex-col gap-4 w-full md:max-w-[780px] md:mx-auto"
    >
      <div
        v-for="i in 3"
        :key="i"
        class="review-card-skeleton"
        :class="{ 'md:hidden': i > 1 }"
      >
        <div class="flex justify-between gap-2 mb-2">
          <div
            class="review-card-skeleton__line review-card-skeleton__line--title loading-effect"
          />
          <div
            class="review-card-skeleton__line review-card-skeleton__line--date loading-effect"
          />
        </div>
        <div
          class="review-card-skeleton__line review-card-skeleton__line--stars loading-effect"
        />
        <div
          class="review-card-skeleton__line review-card-skeleton__line--text loading-effect"
        />
        <div
          class="review-card-skeleton__line review-card-skeleton__line--text review-card-skeleton__line--text-short loading-effect"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ReviewBreakdown } from '~/composables/types/event'
import { hasReviewBreakdownData } from '~/utils/reviewSidebarHelpers'

const {
  averageRating = '',
  reviewCount = 0,
  reviewsLength = 0,
  reviewType = 'event',
  reviewBreakdown = null,
} = defineProps<{
  averageRating?: string
  reviewCount?: number
  reviewsLength?: number
  reviewType?: 'event' | 'partner' | 'third-party'
  reviewBreakdown?: ReviewBreakdown | null
}>()

const getRealReviewsCount = computed(() =>
  reviewCount >= reviewsLength ? reviewCount : reviewsLength
)

const showBreakdown = computed(
  () =>
    !!reviewBreakdown &&
    getRealReviewsCount.value > 0 &&
    hasReviewBreakdownData(reviewBreakdown)
)
</script>

<style lang="css" scoped>
@import '~/assets/css/loading.css';
@reference '~/assets/css/tailwind.css';

.review-card-skeleton {
  @apply bg-white p-2 border border-stone-200 rounded-xl md:min-h-[210px];
}

.review-card-skeleton__line {
  @apply rounded-md;

  &--title {
    @apply h-4 w-32;
  }

  &--date {
    @apply h-3 w-20 shrink-0;
  }

  &--stars {
    @apply h-4 w-24 mt-2;
  }

  &--text {
    @apply h-3 w-full mt-3;
  }

  &--text-short {
    @apply w-4/5 mt-2;
  }
}
</style>
